import React, { Suspense, lazy } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNav } from "./context/NavContext";
import { ErrorBoundary } from "./shared/ErrorBoundary";
import { breadcrumbRouteLoad, captureException } from "@/lib/observability/sentry";

// Wrap React.lazy so each chunk load emits a Sentry breadcrumb
// and chunk-load failures are captured with the route name.
function lazyWithBreadcrumb<T extends React.ComponentType<any>>(
  name: string,
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => {
    breadcrumbRouteLoad(name);
    return factory().catch((err) => {
      captureException(err, { tags: { kind: "lazy-chunk-error", route: name } });
      throw err;
    });
  });
}

// ============================================================
// Lazy page modules — domain split + code-splitting
// ============================================================

// Heavy/existing pages (own files, named exports)
const DashboardPage      = lazyWithBreadcrumb("DashboardPage",     () => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const ProductsPage       = lazyWithBreadcrumb("ProductsPage",      () => import("./pages/ProductsPage").then(m => ({ default: m.ProductsPage })));
const ProductDetailPage  = lazyWithBreadcrumb("ProductDetailPage", () => import("./pages/ProductDetailPage").then(m => ({ default: m.ProductDetailPage })));
const ComparePage        = lazyWithBreadcrumb("ComparePage",       () => import("./pages/ComparePage").then(m => ({ default: m.ComparePage })));
const BodyScanPage       = lazyWithBreadcrumb("BodyScanPage",      () => import("./pages/BodyScanPage").then(m => ({ default: m.BodyScanPage })));
const MyAccountPage      = lazyWithBreadcrumb("MyAccountPage",     () => import("./pages/MyAccountPage").then(m => ({ default: m.MyAccountPage })));
const AdminPanel         = lazyWithBreadcrumb("AdminPanel",        () => import("./AdminPanel").then(m => ({ default: m.AdminPanel })));
const VendorPanel        = lazyWithBreadcrumb("VendorPanel",       () => import("./VendorPanel").then(m => ({ default: m.VendorPanel })));
const NutriCoachHub      = lazyWithBreadcrumb("NutriCoachHub",     () => import("./NutriCoachHub"));

// Inline pages extracted from the former monolith — all default exports, take `s` prop
const StackBuilderPage   = lazyWithBreadcrumb("StackBuilderPage",  () => import("./pages/inline/StackBuilderPage"));
const ScannerPage        = lazyWithBreadcrumb("ScannerPage",       () => import("./pages/inline/ScannerPage"));
const TrackerPage        = lazyWithBreadcrumb("TrackerPage",       () => import("./pages/inline/TrackerPage"));
const RewardsPage        = lazyWithBreadcrumb("RewardsPage",       () => import("./pages/inline/RewardsPage"));
const ReferralPage       = lazyWithBreadcrumb("ReferralPage",      () => import("./pages/inline/ReferralPage"));
const WearablePage       = lazyWithBreadcrumb("WearablePage",      () => import("./pages/inline/WearablePage"));
const CommunityPage      = lazyWithBreadcrumb("CommunityPage",     () => import("./pages/inline/CommunityPage"));
const AiAgentPage        = lazyWithBreadcrumb("AiAgentPage",       () => import("./pages/inline/AiAgentPage"));
const HealthReportPage   = lazyWithBreadcrumb("HealthReportPage",  () => import("./pages/inline/HealthReportPage"));
const LearnPage          = lazyWithBreadcrumb("LearnPage",         () => import("./pages/inline/LearnPage"));
const NotificationsPage  = lazyWithBreadcrumb("NotificationsPage", () => import("./pages/inline/NotificationsPage"));
const CheckoutPage       = lazyWithBreadcrumb("CheckoutPage",      () => import("./pages/inline/CheckoutPage"));
const OrdersPage         = lazyWithBreadcrumb("OrdersPage",        () => import("./pages/inline/OrdersPage"));
const FavoritesPage      = lazyWithBreadcrumb("FavoritesPage",     () => import("./pages/inline/FavoritesPage"));
const ProfilePage        = lazyWithBreadcrumb("ProfilePage",       () => import("./pages/inline/ProfilePage"));
const StoresPage         = lazyWithBreadcrumb("StoresPage",        () => import("./pages/inline/StoresPage"));
const HealthGoalsPage    = lazyWithBreadcrumb("HealthGoalsPage",   () => import("./pages/inline/HealthGoalsPage"));
const MyPlanPage         = lazyWithBreadcrumb("MyPlanPage",        () => import("./pages/inline/MyPlanPage"));
const VendorApplyPage    = lazyWithBreadcrumb("VendorApplyPage",   () => import("./pages/inline/VendorApplyPage"));


// ============================================================
// Prefetch + status broadcast (kept here for backward-compat
// exports consumed by TopBar / AppSidebar / BottomNav / PrefetchStatusIndicator)
// ============================================================

const _prefetchedMap = new Map<string, boolean>();
const _prefetchAttempts = new Map<string, number>();
const _prefetchTimers = new Map<string, ReturnType<typeof setTimeout>>();
const PREFETCH_MAX_ATTEMPTS = 4;
const PREFETCH_BASE_DELAY_MS = 500;

type PrefetchStatus = "idle" | "failed";
const _prefetchStatus = new Map<string, PrefetchStatus>();
const _prefetchListeners = new Set<(snapshot: Record<string, PrefetchStatus>) => void>();
function emitPrefetchStatus() {
  const snap: Record<string, PrefetchStatus> = {};
  _prefetchStatus.forEach((v, k) => { snap[k] = v; });
  _prefetchListeners.forEach((l) => { try { l(snap); } catch { /* no-op */ } });
}
export function subscribePrefetchStatus(listener: (snapshot: Record<string, PrefetchStatus>) => void) {
  _prefetchListeners.add(listener);
  const snap: Record<string, PrefetchStatus> = {};
  _prefetchStatus.forEach((v, k) => { snap[k] = v; });
  listener(snap);
  return () => { _prefetchListeners.delete(listener); };
}
function setPrefetchStatus(key: string, status: PrefetchStatus) {
  if (status === "idle") _prefetchStatus.delete(key);
  else _prefetchStatus.set(key, status);
  emitPrefetchStatus();
}
function readSession(key: string): boolean {
  try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
}
function writeSession(key: string) {
  try { sessionStorage.setItem(key, "1"); } catch { /* no-op */ }
}
function prefetchOnce(key: string, importer: () => Promise<unknown>) {
  if (_prefetchedMap.get(key) || readSession(key)) { _prefetchedMap.set(key, true); return; }
  _prefetchedMap.set(key, true);
  writeSession(key);
  void importer().then(() => { _prefetchAttempts.delete(key); setPrefetchStatus(key, "idle"); })
    .catch(() => {
      _prefetchedMap.delete(key);
      try { sessionStorage.removeItem(key); } catch { /* no-op */ }
      scheduleRetry(key, importer);
    });
}
function scheduleRetry(key: string, importer: () => Promise<unknown>) {
  const attempts = (_prefetchAttempts.get(key) ?? 0) + 1;
  if (attempts >= PREFETCH_MAX_ATTEMPTS) { _prefetchAttempts.delete(key); setPrefetchStatus(key, "failed"); return; }
  _prefetchAttempts.set(key, attempts);
  const existing = _prefetchTimers.get(key);
  if (existing) clearTimeout(existing);
  const jitter = Math.random() * 100;
  const delay = PREFETCH_BASE_DELAY_MS * 2 ** (attempts - 1) + jitter;
  const timer = setTimeout(() => { _prefetchTimers.delete(key); prefetchOnce(key, importer); }, delay);
  _prefetchTimers.set(key, timer);
}
export function prefetchDashboard() {
  prefetchOnce("nm_pf_dashboard", () => import("./pages/DashboardPage"));
}
export function prefetchProducts() {
  prefetchOnce("nm_pf_products", () => import("./pages/ProductsPage"));
}

// ============================================================
// Skeletons / fallbacks
// ============================================================

const PageFallback = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "var(--tx3)" }}>
    <div style={{ width: 28, height: 28, border: "3px solid var(--bd2)", borderTopColor: "var(--ac)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
  </div>
);

function SkeletonGrid({ count = 8 }) {
  return (<div className="prods">{Array.from({ length: count }).map((_, i) => (
    <div key={i} className="skel-card"><div className="skel-img" /><div className="skel-line med" /><div className="skel-line short" /><div className="skel-price" /></div>
  ))}</div>);
}

/**
 * Dashboard fallback shaped to approximate the resolved DashboardPage height
 * (hero block + category grid + product grid). Prevents the ~0.17 CLS that
 * otherwise occurs when the lazy chunk replaces a tiny fallback with a tall
 * hero, pushing every card below into a new vertical position.
 */
function DashboardSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="skeleton" style={{ width: "100%", height: 340, borderRadius: 16 }} />
      <div className="cats" style={{ display: "grid", gap: 10 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 72, borderRadius: 12 }} />
        ))}
      </div>
      <div className="skel-line med" style={{ height: 18, width: "40%" }} />
      <div className="prods">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skel-card"><div className="skel-img" /><div className="skel-line med" /><div className="skel-line short" /><div className="skel-price" /></div>
        ))}
      </div>
      <span className="sr-only">Cargando…</span>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-live="polite" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 24, padding: "8px 0" }}>
      <div>
        <div className="skeleton" style={{ width: "100%", aspectRatio: "1/1", borderRadius: 12 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton" style={{ width: 56, height: 56, borderRadius: 8 }} />)}
        </div>
      </div>
      <div>
        <div className="skel-line med" style={{ height: 14, width: "30%", margin: "4px 0 12px" }} />
        <div className="skel-line" style={{ height: 22, width: "85%", margin: "0 0 10px" }} />
        <div className="skel-line short" style={{ height: 14, margin: "0 0 18px" }} />
        <div className="skel-line" style={{ height: 28, width: "40%", margin: "0 0 16px" }} />
        <div className="skel-line med" style={{ height: 10, margin: "0 0 6px" }} />
        <div className="skel-line" style={{ height: 10, margin: "0 0 6px" }} />
        <div className="skel-line short" style={{ height: 10, margin: "0 0 18px" }} />
        <div className="skel-line" style={{ height: 42, width: "100%", borderRadius: 10 }} />
      </div>
      <span className="sr-only">Cargando producto…</span>
    </div>
  );
}

// ============================================================
// Wrappers
// ============================================================

function Wrap({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={fallback ?? <PageFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

// ============================================================
// Router
// ============================================================

export function NutriMartRoutes(s: any) {
  const { page, pageLoading, selProd, user, userRole, lang, t } = s;
  const { nav } = useNav();

  const renderPage = () => { switch (page) {
    case "dashboard":
      return (
        <Wrap fallback={<DashboardSkeleton />}>
          <DashboardPage
            P={s.P} lang={lang} t={t} addCart={s.addCart} $={s.$}
            pageLoading={pageLoading}
            aiGoal={s.aiGoal} setAiGoal={s.setAiGoal} aiProds={s.aiProds}
            orders={s.orders} wearDevices={s.wearDevices} bioData={s.bioData}
            bodyScanData={s.bodyScanData} agentAlerts={s.agentAlerts}
            commStacks={s.commStacks} setCatF={s.setCatF}
            user={user} userProfile={s.userProfile}
          />
        </Wrap>
      );

    case "products":
      return (
        <Wrap fallback={<SkeletonGrid count={8} />}>
          <ProductsPage
            lang={lang} t={t} filtered={s.filtered} catalog={s.P} pageLoading={pageLoading}
            catF={s.catF} setCatF={s.setCatF} sortBy={s.sortBy} setSortBy={s.setSortBy}
          />
        </Wrap>
      );

    case "detail":
      if (pageLoading) return <DetailSkeleton />;
      if (!selProd) return <DetailSkeleton />;
      return (
        <Wrap fallback={<DetailSkeleton />}>
          <ProductDetailPage
            p={selProd} P={s.P} lang={lang} t={t} $={s.$}
            addCart={s.addCart} togFav={s.togFav} favs={s.favs}
            qty={s.qty} setQty={s.setQty} setAutoships={s.setAutoships} toast={s.toast}
            newReviewText={s.newReviewText} setNewReviewText={s.setNewReviewText}
            newReviewRating={s.newReviewRating} setNewReviewRating={s.setNewReviewRating}
            userReviews={s.userReviews} setUserReviews={s.setUserReviews}
          />
        </Wrap>
      );

    case "stackBuilder":   return <Wrap><StackBuilderPage {...s} /></Wrap>;
    case "scanner":        return <Wrap><ScannerPage {...s} /></Wrap>;
    case "tracker":        return <Wrap><TrackerPage {...s} /></Wrap>;
    case "rewards":        return <Wrap><RewardsPage {...s} /></Wrap>;
    case "referral":       return <Wrap><ReferralPage {...s} /></Wrap>;
    case "wearable":       return <Wrap><WearablePage {...s} /></Wrap>;
    case "community":      return <Wrap><CommunityPage {...s} /></Wrap>;
    case "aiAgent":        return <Wrap><AiAgentPage {...s} /></Wrap>;
    case "healthReport":   return <Wrap><HealthReportPage {...s} /></Wrap>;
    case "learn":          return <Wrap><LearnPage {...s} /></Wrap>;
    case "notifications":  return <Wrap><NotificationsPage {...s} /></Wrap>;
    case "checkout":       return <Wrap><CheckoutPage {...s} /></Wrap>;
    case "orders":         return <Wrap><OrdersPage {...s} /></Wrap>;
    case "favorites":      return <Wrap><FavoritesPage {...s} /></Wrap>;
    case "profile":        return <Wrap><ProfilePage {...s} /></Wrap>;
    case "stores":         return <Wrap><StoresPage {...s} /></Wrap>;
    case "healthGoals":    return <Wrap><HealthGoalsPage {...s} /></Wrap>;
    case "myPlan":         return <Wrap><MyPlanPage {...s} /></Wrap>;
    

    case "aiChat":
    case "nutricoach":
      return (
        <Wrap>
          <NutriCoachHub
            products={s.P}
            lang={lang}
            user={user}
            onAddToCart={s.addCart}
            onViewProduct={(p: any) => nav("detail", p)}
            toast={s.toast}
            defaultTab={page === "nutricoach" ? "recommend" : "chat"}
          />
        </Wrap>
      );

    case "bodyScan":
      return (
        <Wrap>
          <BodyScanPage
            bodyScanData={s.bodyScanData}
            selectedScan={s.selectedScan} setSelectedScan={s.setSelectedScan}
            bsTab={s.bsTab} setBsTab={s.setBsTab}
            compA={s.compA} setCompA={s.setCompA}
            compB={s.compB} setCompB={s.setCompB}
            bodyGoals={s.bodyGoals} setBodyGoals={s.setBodyGoals}
            editingGoals={s.editingGoals} setEditingGoals={s.setEditingGoals}
            goalDraft={s.goalDraft} setGoalDraft={s.setGoalDraft}
            lang={lang} t={t}
            addCart={s.addCart} toast={s.toast} P={s.P}
          />
        </Wrap>
      );

    case "compare":
      return (
        <Wrap>
          <ComparePage
            P={s.P} cmpIds={s.cmpIds} setCmpIds={s.setCmpIds}
            lang={lang} t={t} $={s.$} addCart={s.addCart}
          />
        </Wrap>
      );

    case "myaccount":
      return (
        <Wrap>
          <MyAccountPage
            user={user} userProfile={s.userProfile}
            orders={s.orders} autoships={s.autoships}
            rewardPts={s.rewardPts} refTier={s.refTier}
            P={s.P} lang={lang} setLang={s.setLang} t={t} $={s.$}
            toast={s.toast} setSelOrder={s.setSelOrder}
          />
        </Wrap>
      );

    case "admin":
      return (
        <Wrap>
          <AdminPanel
            adminOrders={s.adminOrders} adminTab={s.adminTab} setAdminTab={s.setAdminTab}
            adminTrackingInputs={s.adminTrackingInputs} setAdminTrackingInputs={s.setAdminTrackingInputs}
            adminSetTracking={s.adminSetTracking} adminCreateShipment={s.adminCreateShipment} adminAdvanceStatus={s.adminAdvanceStatus}
            P={s.P} lang={lang} t={t} mapStatus={s.mapStatus} $={s.$} user={user} toast={s.toast}
          />
        </Wrap>
      );

    case "vendor":
      if (!user || (userRole !== "vendor" && userRole !== "admin")) {
        return <Wrap><VendorApplyPage {...s} /></Wrap>;
      }
      return (
        <Wrap>
          <VendorPanel user={user} lang={lang} t={t} mapStatus={s.mapStatus} $={s.$} toast={s.toast} />
        </Wrap>
      );

    case "vendorApply":
      return <Wrap><VendorApplyPage {...s} /></Wrap>;

    default:
      return null;
  } };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={page}
        className="page-trans"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      >
        {renderPage()}
      </motion.div>
    </AnimatePresence>
  );
}
